import { actorIsWorking, type WorldActor, type WorldPlace, type WorldState } from './living-world';

export type WorldStation = 'research' | 'creation' | 'communication';
export const WORKSTATIONS: { kind: WorldStation; x: number; z: number; angle: number }[] = [
  { kind: 'research', x: -3.2, z: -1.25, angle: Math.PI / 2 },
  { kind: 'creation', x: 0, z: -3.45, angle: 0 },
  { kind: 'communication', x: 3.2, z: -1.25, angle: -Math.PI / 2 },
];

export function stationFor(state: WorldState): WorldStation | undefined {
  if (state === 'reading' || state === 'searching' || state === 'checking') return 'research';
  if (state === 'sending') return 'communication';
  if (actorIsWorking(state)) return 'creation';
  return undefined;
}

export const restingState = (state: WorldState) => ['waiting', 'queued', 'scheduled'].includes(state);

/** Stable task ordering keeps a refetch from shuffling every robot's seat. */
export function arrangeWorldActors(actors: WorldActor[], places: WorldPlace[]): WorldActor[] {
  const slots = new Map<string, number>();
  const positions = new Map<string, { x: number; z: number }>();
  const byAgent = new Map(places.map(place => [place.id, place]));
  for (const actor of [...actors].sort((a, b) => a.id.localeCompare(b.id))) {
    const place = byAgent.get(actor.agentId);
    if (!place || actor.state === 'review' || actor.state === 'done') continue;
    const station = stationFor(actor.state);
    const key = `${actor.agentId}:${station || (restingState(actor.state) ? 'rest' : 'attention')}`;
    const slot = slots.get(key) || 0; slots.set(key, slot + 1);
    let x: number, z: number;
    if (station) {
      const desk = WORKSTATIONS.find(item => item.kind === station)!;
      const across = ((slot % 3) - 1) * 1.12;
      const forward = 0.85 + Math.floor(slot / 3) * 1.12;
      x = desk.x + Math.cos(desk.angle) * across + Math.sin(desk.angle) * forward;
      z = desk.z - Math.sin(desk.angle) * across + Math.cos(desk.angle) * forward;
    } else if (restingState(actor.state)) {
      x = ((slot % 4) - 1.5) * 1.1;
      z = 0.65 + Math.floor(slot / 4) * 1.15;
    } else {
      x = (slot % 3 - 1) * 1.15; z = -0.35 - Math.floor(slot / 3) * 1.15;
    }
    positions.set(actor.id, { x: place.x + x, z: place.z + z });
  }
  return actors.map(actor => ({ ...actor, ...positions.get(actor.id) }));
}

/** The full task list stays navigable; only physical residents have a density budget. */
export function worldVisibleActors(actors: WorldActor[], selected: string | null, total: number): WorldActor[] {
  const perAgent = new Map<string, number>();
  const priority = (actor: WorldActor) => actor.id === selected || `review:${actor.id}` === selected ? -1
    : actor.state === 'needsYou' ? 0 : actor.state === 'failed' ? 1 : actor.state === 'review' ? 2 : actorIsWorking(actor.state) ? 3 : 4;
  return actors.filter(actor => !!actor.agentId).sort((a, b) => priority(a) - priority(b) || a.id.localeCompare(b.id)).filter(actor => {
    const count = perAgent.get(actor.agentId) || 0; perAgent.set(actor.agentId, count + 1); return count < 8;
  }).slice(0, total);
}

export function worldAreaCounts(actors: WorldActor[], agentId: string) {
  const own = actors.filter(actor => actor.agentId === agentId && actor.state !== 'done');
  return { working: own.filter(actor => actorIsWorking(actor.state) || actor.state === 'review').length,
    waiting: own.filter(actor => !actorIsWorking(actor.state) && actor.state !== 'review').length,
    needsYou: own.filter(actor => actor.state === 'needsYou').length, failed: own.filter(actor => actor.state === 'failed').length };
}
